import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProviderPriceListItemOptionValue, {
  useProviderPriceListItemOptionValueForm,
} from '@hooks/useProviderPriceListItemOptionValue'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditProviderPriceListItemOptionValueModal'

type ModalCallbacks = {
  closeModal: () => void,
  createProviderPriceListItemOptionValue: (payload: HandleCallbackActionParams) => void,
  updateProviderPriceListItemOptionValue: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditProviderPriceListItemOptionValueModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createProviderPriceListItemOptionValue, updateProviderPriceListItemOptionValue } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const providerPriceListItemOptionValue = digObject(modalPayload, 'providerPriceListItemOptionValue', {})

  const newRecord = !providerPriceListItemOptionValue.id

  const {
    callbacks: {
      createProviderPriceListItemOptionValue: createFn,
      updateProviderPriceListItemOptionValue: updateFn,
    },
    creating,
    updating,
  } = useProviderPriceListItemOptionValue(providerPriceListItemOptionValue)

  const {
    entityState,
    entityState: {
      price,
      product_code,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useProviderPriceListItemOptionValueForm(providerPriceListItemOptionValue, { validateOn: 'change' })

  const createOrUpdateProviderPriceListItemOptionValuePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Price List Item Option Value ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Option Value"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Price" errorMessage={errors.price} marginTop="large">
            <input name="price" type="number" value={price} {...handlers} />
          </Form.Field>

          <Form.Field label="Product Code" errorMessage={errors.product_code} marginTop="large">
            <input name="product_code" type="text" value={product_code} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createProviderPriceListItemOptionValue(createOrUpdateProviderPriceListItemOptionValuePayload)
              : () => updateProviderPriceListItemOptionValue(createOrUpdateProviderPriceListItemOptionValuePayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Item Option Value` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderPriceListItemOptionValueModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
